// Inventario de equipos de una empresa/cliente: lista, areas, busqueda, estado,
// impresion/Excel, script y recolector offline, importacion, equipo manual,
// unificar duplicados y la ficha del equipo.
// Antes habia dos copias (TabEquipos en Clientes.js y el bloque de inventario de
// EmpresaDetalle en TecnicoPage.js) que ya habian divergido. Los datos son los
// mismos (tabla tecnico_inventario_equipos); aqui vive un solo comportamiento.
//
// Props:
//   empresa      nombre de la empresa/cliente
//   fuente       'tecnico' (valida visibilidad por usuario) | 'clientes' (listado completo)
//   canDelete    muestra eliminar equipo en la ficha
//   canUnificar  muestra unificar equipos duplicados en la ficha
//   onCambio     (lista) => void, se llama cada vez que cambia el inventario cargado
import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import FichaEquipo from './FichaEquipo';
import {
  colorEquipoStyle, SelectorColorEquipo, LicenciaOfficeLinea, imprimirInventarioEmpresa, exportarInventarioExcel,
} from '../lib/equipos';

const C = { accent: '#00b8e3', border: '#dce6ef', text: '#0b304b', muted: '#667b92', green: '#10b981' };

const btnSm = (bg = C.accent) => ({ padding: '6px 12px', borderRadius: 6, border: 'none', background: bg, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' });
const btn = (bg = C.accent) => ({ padding: '10px 18px', borderRadius: 8, border: 'none', background: bg, color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' });
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #cfdde8', background: '#fff', color: C.text, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const badge = { display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '4px 8px', background: '#e7eef6', color: '#47617c', fontSize: 11, fontWeight: 800, marginLeft: 4 };
const menuItem = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 16px', fontSize: 13, fontWeight: 600, color: '#47617c', cursor: 'pointer', fontFamily: 'inherit' };

const normalizar = valor => String(valor || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('es');
const fechaInput = v => (v ? String(v).slice(0, 10) : '');
const dinero = value => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '-';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
};

const equipoFormVacio = () => ({
  nombre_equipo: '', responsable: '', area: '', usuario_windows: '',
  windows: '', windows_version: '', serial: '', procesador: '', memoria_ram_gb: '',
  tarjeta_video: '', discos: '', mac_wifi: '', mac_ethernet: '',
  ultimo_mantenimiento: '', valor_comercial: '', office_version: '', office_serial: '',
  observaciones_creacion: '',
});

const CAMPOS_NUEVO = [
  ['nombre_equipo', 'Equipo'], ['responsable', 'Responsable'], ['area', 'Area'],
  ['usuario_windows', 'Usuario'], ['windows', 'Sistema operativo'], ['windows_version', 'Version'],
  ['serial', 'Serial'], ['procesador', 'Procesador'], ['memoria_ram_gb', 'RAM GB'],
  ['tarjeta_video', 'Tarjeta de video'], ['discos', 'Discos duros'], ['mac_wifi', 'MAC WiFi'],
  ['mac_ethernet', 'MAC Ethernet'], ['ultimo_mantenimiento', 'Ultimo mantenimiento'], ['valor_comercial', 'Valor comercial'],
  ['office_version', 'Office version'], ['office_serial', 'Office serial'],
];

function useAnchoVentana() {
  const [ancho, setAncho] = useState(() => (typeof window === 'undefined' ? 1130 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setAncho(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return ancho;
}

function ModalUnificar({ equipo, equipos, onConfirm, onClose }) {
  const [masterId, setMasterId] = useState('');
  const otros = equipos.filter(e => e.id !== equipo.id);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 4, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#08293f', marginBottom: 6 }}>Unificar equipos</div>
        <div style={{ fontSize: 12, color: '#5a7a8a', marginBottom: 16 }}>
          El equipo <strong>{equipo.nombre_equipo || equipo.identidad_valor}</strong> se marcará como duplicado y <strong>todo su historial</strong> pasará al equipo que selecciones.
        </div>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#5a7a8a', display: 'block', marginBottom: 4 }}>Equipo master (el que queda)</label>
        <select value={masterId} onChange={e => setMasterId(e.target.value)} className="input" style={{ background: '#f9fafb', border: '1px solid #e8eef2', marginBottom: 16 }}>
          <option value="">Seleccionar...</option>
          {otros.map(e => <option key={e.id} value={e.id}>{e.nombre_equipo || e.identidad_valor} {e.serial ? `· SN:${e.serial}` : ''}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={!masterId} onClick={() => onConfirm(parseInt(masterId))} className="btn-primary" style={{ padding: '8px 16px', fontSize: 12, background: '#b45309', borderColor: '#b45309' }}>Unificar</button>
          <button onClick={onClose} className="btn-secondary" style={{ padding: '8px 16px', fontSize: 12 }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function descargarBlob(r, nombreDefecto) {
  const cd = r.headers['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/i);
  const url = window.URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = match?.[1] || nombreDefecto;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Los errores de endpoints con responseType blob llegan como Blob: se lee el JSON.
async function mensajeError(err, porDefecto) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).error || porDefecto; } catch (_) { return porDefecto; }
  }
  return data?.error || porDefecto;
}

export default function InventarioEquipos({ empresa, fuente = 'tecnico', canDelete = false, canUnificar = false, onCambio }) {
  const ancho = useAnchoVentana();
  const isPhone = ancho < 640;
  const empresaNorm = String(empresa || '').trim().toUpperCase();
  const empresaRelacionada = empresaNorm === 'NODO' ? 'ANTIGRAFO' : ['ANTIGRAFO', 'PLASMA'].includes(empresaNorm) ? 'NODO' : null;
  const empresaRelacionadaLabel = empresaNorm === 'NODO' ? 'PLASMA (ANTIGRAFO)' : empresaRelacionada;
  const puedeCombinar = !!empresaRelacionada;

  const [inventario, setInventario] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [expandido, setExpandido] = useState(true);
  const [combinado, setCombinado] = useState(() => {
    try { return localStorage.getItem(`age_inventario_combinado_${empresaNorm}`) === '1'; } catch (_) { return false; }
  });
  const [vista, setVista] = useState(() => {
    try {
      const guardada = localStorage.getItem(`age_tecnico_inventario_vista_${empresa}`);
      if (guardada) return guardada === 'areas' ? 'areas' : 'lista';
    } catch (_) {}
    return typeof window !== 'undefined' && window.innerWidth < 640 ? 'areas' : 'lista';
  });
  const [areasAbiertas, setAreasAbiertas] = useState({});
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('todos');
  const [importando, setImportando] = useState(false);
  const inputOfflineRef = useRef(null);
  const [equipoActivo, setEquipoActivo] = useState(null);
  const [unificando, setUnificando] = useState(null);
  const [nuevoEquipo, setNuevoEquipo] = useState(false);
  const [guardandoNuevo, setGuardandoNuevo] = useState(false);
  const [nuevoForm, setNuevoForm] = useState(equipoFormVacio);
  const [menuInventario, setMenuInventario] = useState(false);
  const [menuImprimir, setMenuImprimir] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      let lista;
      if (combinado && empresaRelacionada) {
        lista = (await API.get('/tecnico/inventario-combinado', { params: { empresa } })).data;
      } else {
        const r = fuente === 'clientes'
          ? await API.get(`/clientes/${encodeURIComponent(empresa)}/inventario`)
          : await API.get('/tecnico/inventario', { params: { empresa } });
        lista = Array.isArray(r.data) ? r.data.map(eq => ({ ...eq, empresa_origen: eq.empresa || empresa })) : [];
      }
      setInventario(Array.isArray(lista) ? lista : []);
    } catch (_) {
      setInventario([]);
    }
    setCargando(false);
  }, [empresa, fuente, combinado, empresaRelacionada]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { try { localStorage.setItem(`age_tecnico_inventario_vista_${empresa}`, vista); } catch (_) {} }, [empresa, vista]);
  useEffect(() => { try { localStorage.setItem(`age_inventario_combinado_${empresaNorm}`, combinado ? '1' : '0'); } catch (_) {} }, [empresaNorm, combinado]);

  const onCambioRef = useRef(onCambio);
  useEffect(() => { onCambioRef.current = onCambio; });
  useEffect(() => { if (onCambioRef.current) onCambioRef.current(inventario); }, [inventario]);

  const origenDe = eq => eq.empresa_origen || eq.empresa || empresa;

  const cambiarColor = async (eq, clasificacion_color) => {
    try {
      await API.put(`/inventario/clasificacion/tecnico/${eq.id}`, { clasificacion_color });
      setInventario(list => list.map(item => item.id === eq.id && origenDe(item) === origenDe(eq) ? { ...item, clasificacion_color } : item));
      setEquipoActivo(actual => (actual?.id === eq.id ? { ...actual, clasificacion_color } : actual));
    } catch (err) {
      alert(await mensajeError(err, 'No se pudo guardar el color'));
    }
  };

  const guardarNuevo = async () => {
    setGuardandoNuevo(true);
    try {
      const { data } = await API.post('/tecnico/inventario', { ...nuevoForm, empresa });
      setNuevoEquipo(false);
      if (data.equipo) setEquipoActivo(data.equipo);
      cargar();
    } catch (err) {
      alert(await mensajeError(err, 'No se pudo crear el equipo'));
    } finally {
      setGuardandoNuevo(false);
    }
  };

  const eliminarEquipo = async eq => {
    try {
      await API.delete(`/tecnico/inventario/${eq.id}`);
      setEquipoActivo(null);
      cargar();
    } catch (err) {
      alert(await mensajeError(err, 'No se pudo eliminar el equipo'));
    }
  };

  const confirmarUnificacion = async masterId => {
    try {
      await API.post('/clientes/inventario/unificar', { master_id: masterId, duplicado_id: unificando.id });
      if (equipoActivo && equipoActivo.id === unificando.id) setEquipoActivo(null);
      setUnificando(null);
      cargar();
    } catch (err) {
      alert(await mensajeError(err, 'Error al unificar'));
    }
  };

  const descargarScript = async () => {
    try {
      const r = await API.get('/tecnico/script-inventario', { params: { empresa }, responseType: 'blob' });
      descargarBlob(r, `inventario-${empresa.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'empresa'}.cmd`);
    } catch (err) {
      alert(await mensajeError(err, 'No se pudo generar el script'));
    }
  };

  const descargarRecolectorOffline = async () => {
    try {
      const r = await API.get('/tecnico/script-inventario-offline', { params: { empresa }, responseType: 'blob' });
      descargarBlob(r, `recolector-offline-${empresa.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()}.cmd`);
    } catch (err) {
      alert(await mensajeError(err, 'No se pudo generar el recolector offline'));
    }
  };

  const importarOffline = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setImportando(true);
    try {
      const archivos = [];
      for (const file of files) {
        try {
          archivos.push({ nombre: file.name, datos: JSON.parse(await file.text()) });
        } catch (_) {
          archivos.push({ nombre: file.name, datos: null });
        }
      }
      const { data: d } = await API.post('/tecnico/inventario/importar-offline', { empresa, archivos });
      const partes = [`${d.creados || 0} creados`, `${d.actualizados || 0} actualizados`, `${d.sin_cambios || 0} sin cambios`];
      if (d.errores?.length) partes.push(`${d.errores.length} con error`);
      alert(`Importacion terminada: ${partes.join(', ')}.${d.errores?.length ? `\n\n${d.errores.map(x => `${x.archivo}: ${x.error}`).join('\n')}` : ''}`);
      cargar();
    } catch (err) {
      alert(await mensajeError(err, 'No se pudieron importar los archivos'));
    } finally {
      setImportando(false);
    }
  };

  const termino = normalizar(busqueda.trim());
  const coincideBusqueda = eq => {
    if (!termino) return true;
    return [
      eq.nombre_equipo, eq.identidad_valor, eq.responsable, eq.usuario_windows, eq.area,
      eq.serial, eq.windows, eq.windows_version, eq.procesador, eq.mac_wifi,
      eq.mac_ethernet, eq.empresa_origen, eq.empresa,
      eq.office_licencia, String(eq.office_licencia || '').replace(/-/g, ''), eq.office_serial, eq.correo_electronico,
      eq.office_caja ? `caja ${eq.office_caja}` : '',
    ].filter(Boolean).map(normalizar).join(' ').includes(termino.replace(/\s+$/, ''));
  };
  const filtrado = inventario.filter(eq => {
    const est = eq.clasificacion_color || '';
    if (estado === 'todos' ? (est === 'baja' || est === 'pendiente') : est !== estado) return false;
    return coincideBusqueda(eq);
  });
  // Pendientes (azul) y dados de baja (gris): salen de la lista principal y se muestran en secciones aparte, al final
  const SECCIONES = [
    { clave: 'pendiente', titulo: 'Pendientes', borde: '#64b5f6', fondo: '#eef6fd', cabecera: '#dbeafe', texto: '#1e5aa8', tachado: false },
    { clave: 'baja', titulo: 'Dados de baja', borde: '#9ca3af', fondo: '#f3f4f6', cabecera: '#e5e7eb', texto: '#4b5563', tachado: true },
  ].map(sec => ({ ...sec, items: estado === 'todos' ? inventario.filter(eq => eq.clasificacion_color === sec.clave && coincideBusqueda(eq)) : [] }));
  const hayEspeciales = SECCIONES.some(sec => sec.items.length > 0);
  // Para imprimir y exportar: la lista principal y, al final, pendientes y dados de baja
  const paraImprimir = [...filtrado, ...SECCIONES.flatMap(sec => sec.items)];
  const porArea = filtrado.reduce((acc, eq) => {
    const area = (eq.area || 'Sin area').trim() || 'Sin area';
    if (!acc[area]) acc[area] = [];
    acc[area].push(eq);
    return acc;
  }, {});
  const areas = Object.entries(porArea).sort((a, b) => a[0].localeCompare(b[0], 'es'));
  const toggleArea = area => setAreasAbiertas(prev => ({ ...prev, [area]: !(prev[area] ?? true) }));
  const setTodasAreas = abierta => setAreasAbiertas(Object.fromEntries(areas.map(([area]) => [area, abierta])));

  const modalBox = maxWidth => ({ width: '100%', maxWidth, maxHeight: isPhone ? '92vh' : '86vh', overflow: 'auto', background: '#fff', color: '#12324d', border: `1px solid ${C.border}`, borderRadius: isPhone ? 10 : 14, padding: isPhone ? 14 : 20, boxSizing: 'border-box' });
  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: 16 };

  return (
    <section id="inventario-tecnico" style={{ background: '#fff', color: '#12324d', border: '1px solid #e8eef2', borderRadius: 4, padding: '14px 16px', marginBottom: 12, boxShadow: '0 1px 2px rgba(8,41,63,0.08)', scrollMarginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isPhone ? 'stretch' : 'center', gap: 10, marginBottom: 10, flexDirection: isPhone ? 'column' : 'row' }}>
        <button type="button" onClick={() => setExpandido(v => !v)} style={{ border: 'none', background: 'transparent', textAlign: 'left', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#5a7a8a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{'\u2014'}</span> Inventario <span style={badge}>{inventario.length}</span>
          </div>
          <div style={{ color: C.muted, fontSize: 12 }}>
            Equipos detectados por script, cambios y bitacora por PC.
            {puedeCombinar && combinado ? ` Incluye ${empresaRelacionadaLabel}.` : ''}
          </div>
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: isPhone ? 'flex-start' : 'flex-end' }}>
          {expandido && (
            <>
              <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden' }}>
                {[['lista', 'Lista'], ['areas', 'Areas']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setVista(key)} style={{ border: 'none', background: vista === key ? '#143a57' : '#fff', color: vista === key ? '#fff' : '#47617c', fontWeight: 900, fontSize: 12, padding: '9px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
                ))}
              </div>
              {vista === 'areas' && (
                <div style={{ display: 'flex', gap: 5 }}>
                  <button type="button" onClick={() => setTodasAreas(true)} style={{ ...btnSm('#e7eef6'), color: '#143a57', padding: '8px 10px' }}>Expandir</button>
                  <button type="button" onClick={() => setTodasAreas(false)} style={{ ...btnSm('#e7eef6'), color: '#143a57', padding: '8px 10px' }}>Contraer</button>
                </div>
              )}
              {puedeCombinar && (
                <button type="button" onClick={() => setCombinado(v => !v)} style={{ ...btnSm(combinado ? C.green : '#e7eef6'), color: combinado ? '#fff' : '#143a57', padding: '8px 10px' }}>
                  {combinado ? `Viendo ${empresa} + ${empresaRelacionadaLabel}` : `Ver tambien ${empresaRelacionadaLabel}`}
                </button>
              )}
            </>
          )}
          <input ref={inputOfflineRef} type="file" multiple accept=".json,.ageinv.json,application/json" onChange={importarOffline} style={{ display: 'none' }} />
          {menuImprimir && <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMenuImprimir(false)} />}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setMenuImprimir(v => !v)} style={{ ...btn('#fff'), ...(isPhone ? { padding: '8px 12px', fontSize: 13 } : {}), color: '#143a57', border: '1px solid #cbd9e6' }}>Imprimir {menuImprimir ? '▲' : '▾'}</button>
            {menuImprimir && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 0', minWidth: 220, boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
                <button onClick={() => { imprimirInventarioEmpresa({ empresa, titulo: `Inventario ${empresa}`, equipos: paraImprimir, modo: 'completo' }); setMenuImprimir(false); }} style={{ ...menuItem, fontWeight: 700, color: C.text }}>
                  Inventario completo ({filtrado.length})
                </button>
                <button onClick={() => { imprimirInventarioEmpresa({ empresa, titulo: `Listado ${empresa}`, equipos: paraImprimir, modo: 'resumen' }); setMenuImprimir(false); }} style={menuItem}>
                  Solo el listado
                </button>
                <button onClick={() => { imprimirInventarioEmpresa({ empresa, titulo: `Fichas ${empresa}`, equipos: paraImprimir, modo: 'fichas' }); setMenuImprimir(false); }} style={menuItem}>
                  Solo las fichas (2 por hoja)
                </button>
                <button onClick={() => { exportarInventarioExcel({ nombre: empresa, titulo: `Inventario ${empresa}`, equipos: paraImprimir }); setMenuImprimir(false); }} style={{ ...menuItem, fontWeight: 700, color: '#059669' }}>
                  Exportar Excel ({filtrado.length})
                </button>
                {areas.length > 1 && <div style={{ borderTop: '1px solid #eef2f6', margin: '4px 0' }} />}
                {areas.length > 1 && areas.map(([area, eqs]) => (
                  <div key={area}>
                    <button onClick={() => { imprimirInventarioEmpresa({ empresa, titulo: `${empresa} - ${area}`, equipos: eqs, modo: 'completo', area }); setMenuImprimir(false); }} style={{ ...menuItem, padding: '7px 16px 4px 22px', fontSize: 12, fontWeight: 500 }}>
                      {area} ({eqs.length})
                    </button>
                    <button onClick={() => { exportarInventarioExcel({ nombre: empresa, titulo: `${empresa} - ${area}`, equipos: eqs }); setMenuImprimir(false); }} style={{ ...menuItem, padding: '3px 16px 7px 34px', fontSize: 11, fontWeight: 700, color: '#059669' }}>
                      Excel {area}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {menuInventario && <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMenuInventario(false)} />}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setMenuInventario(v => !v)} style={{ ...btn('#143a57'), ...(isPhone ? { padding: '8px 12px', fontSize: 13 } : {}) }}>Inventario {menuInventario ? '▲' : '▾'}</button>
            {menuInventario && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 0', minWidth: 190, boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
                {[
                  { label: '+ Equipo manual', action: () => { setNuevoForm(equipoFormVacio()); setNuevoEquipo(true); setMenuInventario(false); }, dark: true },
                  { label: 'Recolector offline', action: () => { descargarRecolectorOffline(); setMenuInventario(false); } },
                  { label: importando ? 'Subiendo...' : 'Subir archivos', action: () => { inputOfflineRef.current?.click(); setMenuInventario(false); }, disabled: importando },
                  { label: 'Descargar script', action: () => { descargarScript(); setMenuInventario(false); }, accent: true },
                ].map(({ label, action, dark, accent, disabled }) => (
                  <button key={label} onClick={action} disabled={disabled} style={{ ...menuItem, fontWeight: dark ? 800 : 600, color: accent ? C.accent : dark ? C.text : '#47617c', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {expandido && inventario.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : 'minmax(240px, 1fr) minmax(210px, 280px)', gap: 8, marginBottom: 12 }}>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar equipo, responsable, serial, área, licencia Office..." style={inputStyle} />
          <select value={estado} onChange={e => setEstado(e.target.value)} style={inputStyle}>
            <option value="todos">Todos los estados</option>
            <option value="">Sin categorizar</option>
            <option value="verde">Verde · Mantenimiento al día</option>
            <option value="magenta">Magenta · Crítico para cambio</option>
            <option value="pendiente">Azul · Pendientes</option>
            <option value="baja">Gris · Dados de baja</option>
          </select>
          {(busqueda || estado !== 'todos') && (
            <div style={{ gridColumn: '1 / -1', color: C.muted, fontSize: 11 }}>
              Mostrando <b style={{ color: C.text }}>{filtrado.length}</b> de {inventario.length} equipos
            </div>
          )}
        </div>
      )}

      {expandido && cargando && <div style={{ padding: '18px 10px', color: C.muted }}>Cargando equipos...</div>}

      {expandido && !cargando && vista === 'lista' && filtrado.length > 0 && (
        <div style={{ overflowX: 'auto', overflowY: filtrado.length > 10 ? 'auto' : 'visible', maxHeight: filtrado.length > 10 ? 520 : 'none', border: filtrado.length > 10 ? '1px solid #e0e9f2' : 'none', borderRadius: filtrado.length > 10 ? 8 : 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1700 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['', 'Empresa', 'Equipo', 'Responsable', 'Usuario', 'Sistema operativo', 'Licencia Office', 'Serial', 'Procesador', 'RAM', 'MAC', 'Ultimo mantenimiento', 'Valor comercial'].map((h, i) => (
                  <th key={`${h}-${i}`} style={{ padding: '8px 8px', textAlign: 'left', color: '#7b8fa7', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrado.map(eq => (
                <tr key={`${origenDe(eq)}-${eq.id}`} style={{ borderBottom: '1px solid #e6eef5', ...colorEquipoStyle(eq) }}>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'stretch' }}>
                      <button onClick={() => setEquipoActivo(eq)} style={{ ...btnSm(C.accent), padding: '5px 8px', fontSize: 11 }}>Ver</button>
                      <SelectorColorEquipo equipo={eq} compacto onChange={color => cambiarColor(eq, color)} />
                    </div>
                  </td>
                  <td style={{ padding: '8px', color: '#274762', fontWeight: 800 }}>{origenDe(eq)}</td>
                  <td style={{ padding: '8px', fontWeight: 900, color: C.text }}>{eq.nombre_equipo || eq.identidad_valor || 'Sin nombre'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{eq.responsable || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{eq.usuario_windows || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{[eq.windows, eq.windows_version].filter(Boolean).join(' - ') || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}><LicenciaOfficeLinea equipo={eq} /></td>
                  <td style={{ padding: '8px', color: '#274762' }}>{eq.serial || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{eq.procesador || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{eq.memoria_ram_gb ? `${eq.memoria_ram_gb} GB` : '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{[eq.mac_wifi ? `WiFi: ${eq.mac_wifi}` : '', eq.mac_ethernet ? `Eth: ${eq.mac_ethernet}` : ''].filter(Boolean).join(' / ') || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762' }}>{fechaInput(eq.ultimo_mantenimiento) || '-'}</td>
                  <td style={{ padding: '8px', color: '#274762', fontWeight: 800 }}>{dinero(eq.valor_comercial)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expandido && !cargando && vista === 'areas' && filtrado.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {areas.map(([area, items]) => (
            <div key={area} style={{ border: '1px solid #e0e9f2', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
              <button type="button" onClick={() => toggleArea(area)} style={{ width: '100%', border: 'none', background: '#f7fafd', padding: '12px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.text, fontWeight: 900, fontSize: 13 }}>
                    <span style={{ color: C.accent, width: 12 }}>{(areasAbiertas[area] ?? true) ? '-' : '+'}</span>
                    <span>{area}</span>
                  </div>
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{items.length} equipo{items.length === 1 ? '' : 's'}</div>
                </div>
              </button>
              {(areasAbiertas[area] ?? true) && (
                <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                  {items.map(eq => (
                    <div key={`${origenDe(eq)}-${eq.id}`} style={{ padding: 12, borderTop: '1px solid #e6eef5', borderRight: isPhone ? 'none' : '1px solid #e6eef5', ...colorEquipoStyle(eq) }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 900, color: C.text, fontSize: 13 }}>{eq.nombre_equipo || eq.identidad_valor || 'Sin nombre'}</div>
                          <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{origenDe(eq)} · {eq.responsable || 'Sin responsable'}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'stretch' }}>
                          <button onClick={() => setEquipoActivo(eq)} style={{ ...btnSm(C.accent), padding: '5px 8px', fontSize: 11 }}>Ver</button>
                          <SelectorColorEquipo equipo={eq} compacto onChange={color => cambiarColor(eq, color)} />
                        </div>
                      </div>
                      <div style={{ marginTop: 9, display: 'grid', gap: 4, color: '#274762', fontSize: 11 }}>
                        <div><b style={{ color: C.text }}>Usuario:</b> {eq.usuario_windows || '-'}</div>
                        <div><b style={{ color: C.text }}>Serial:</b> {eq.serial || '-'}</div>
                        <div><b style={{ color: C.text }}>RAM:</b> {eq.memoria_ram_gb ? `${eq.memoria_ram_gb} GB` : '-'}</div>
                        <div><b style={{ color: C.text }}>CPU:</b> {eq.procesador || '-'}</div>
                        <div><b style={{ color: C.text }}>Office:</b> <LicenciaOfficeLinea equipo={eq} /></div>
                        <div><b style={{ color: C.text }}>Valor:</b> {dinero(eq.valor_comercial)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {expandido && !cargando && SECCIONES.filter(sec => sec.items.length > 0).map(sec => (
        <div key={sec.clave} style={{ marginTop: 16, border: `1px solid ${sec.borde}`, borderRadius: 8, overflow: 'hidden', background: sec.fondo }}>
          <div style={{ background: sec.cabecera, color: sec.texto, padding: '10px 14px', fontWeight: 900, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {sec.titulo} ({sec.items.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 720 }}>
              <tbody>
                {sec.items.map(eq => (
                  <tr key={`${sec.clave}-${origenDe(eq)}-${eq.id}`} style={{ borderTop: `1px solid ${sec.borde}`, color: sec.texto }}>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={() => setEquipoActivo(eq)} style={{ ...btnSm(C.accent), padding: '5px 8px', fontSize: 11 }}>Ver</button>
                        <SelectorColorEquipo equipo={eq} compacto onChange={color => cambiarColor(eq, color)} />
                      </div>
                    </td>
                    <td style={{ padding: '8px', fontWeight: 900, textDecoration: sec.tachado ? 'line-through' : 'none' }}>{eq.nombre_equipo || eq.identidad_valor || 'Sin nombre'}</td>
                    <td style={{ padding: '8px' }}>{origenDe(eq)}</td>
                    <td style={{ padding: '8px' }}>{eq.responsable || '-'}</td>
                    <td style={{ padding: '8px' }}>{eq.serial || '-'}</td>
                    <td style={{ padding: '8px' }}><LicenciaOfficeLinea equipo={eq} /></td>
                    <td style={{ padding: '8px' }}>{eq.observaciones_entrega || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {expandido && !cargando && inventario.length > 0 && filtrado.length === 0 && !hayEspeciales && (
        <div style={{ padding: '24px 12px', textAlign: 'center', color: C.muted, border: '1px solid #e0e9f2', background: '#fff', borderRadius: 8 }}>
          No hay equipos que coincidan con la búsqueda y el estado seleccionado.
        </div>
      )}
      {expandido && !cargando && inventario.length === 0 && (
        <div style={{ padding: '18px 10px', color: C.muted }}>Aun no hay equipos registrados. Descarga y ejecuta el script en el equipo para ingresarlo.</div>
      )}

      {nuevoEquipo && (
        <div style={overlay}>
          <div style={modalBox(920)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, color: C.text, fontSize: isPhone ? 20 : 24 }}>Agregar equipo manual</h3>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>{empresa}</div>
              </div>
              <button onClick={() => setNuevoEquipo(false)} style={{ background: 'transparent', border: 'none', fontSize: 24, color: C.muted, cursor: 'pointer' }}>x</button>
            </div>
            <div style={{ background: '#f7fafd', border: `1px solid ${C.border}`, borderRadius: 9, padding: 12 }}>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>El equipo debe tener serial o una MAC para poder identificarlo.</div>
              <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                {CAMPOS_NUEVO.map(([key, label]) => (
                  <label key={key} style={{ display: 'grid', gap: 4 }}>
                    <span style={{ color: C.muted, fontSize: 11, fontWeight: 800 }}>{label}</span>
                    <input
                      type={key === 'ultimo_mantenimiento' ? 'date' : key === 'valor_comercial' || key === 'memoria_ram_gb' ? 'number' : 'text'}
                      value={nuevoForm[key] || ''}
                      onChange={e => setNuevoForm(f => ({ ...f, [key]: e.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                ))}
                <label style={{ display: 'grid', gap: 4, gridColumn: isPhone ? 'auto' : '1 / -1' }}>
                  <span style={{ color: C.muted, fontSize: 11, fontWeight: 800 }}>Observaciones de creacion</span>
                  <textarea value={nuevoForm.observaciones_creacion || ''} onChange={e => setNuevoForm(f => ({ ...f, observaciones_creacion: e.target.value }))} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button onClick={guardarNuevo} disabled={guardandoNuevo} style={btn(C.accent)}>{guardandoNuevo ? 'Guardando...' : 'Crear equipo'}</button>
                <button onClick={() => setNuevoEquipo(false)} style={btn('#1e3a50')}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {equipoActivo && (
        <div style={overlay}>
          <div style={modalBox(920)}>
            <FichaEquipo
              equipo={equipoActivo}
              nombre={origenDe(equipoActivo)}
              onClose={() => setEquipoActivo(null)}
              onRecargar={cargar}
              onUnificar={canUnificar ? e => setUnificando(e) : undefined}
              canDelete={canDelete}
              onEliminar={eliminarEquipo}
              canMover
            />
          </div>
        </div>
      )}

      {unificando && <ModalUnificar equipo={unificando} equipos={inventario} onConfirm={confirmarUnificacion} onClose={() => setUnificando(null)} />}
    </section>
  );
}
